import React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MatrixItem {
  label: string;
  description?: string;
}

interface QuadrantConfig {
  label: string;
  color?: string;
  items?: MatrixItem[];
}

interface TwoByTwoMatrixProps {
  title?: string;
  xAxisLabel: string;
  yAxisLabel: string;
  xAxisLow?: string;
  xAxisHigh?: string;
  yAxisLow?: string;
  yAxisHigh?: string;
  quadrants: {
    topLeft: QuadrantConfig;
    topRight: QuadrantConfig;
    bottomLeft: QuadrantConfig;
    bottomRight: QuadrantConfig;
  };
}

// ---------------------------------------------------------------------------
// Color mapping
// ---------------------------------------------------------------------------

const QUADRANT_COLORS: Record<string, { bg: string; border: string; text: string; label: string }> = {
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', label: 'text-emerald-700' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-800',   label: 'text-amber-700' },
  red:     { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-800',     label: 'text-red-700' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-800',    label: 'text-blue-700' },
  gray:    { bg: 'bg-gray-50',    border: 'border-gray-200',    text: 'text-gray-700',    label: 'text-gray-600' },
  accent:  { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-800',  label: 'text-indigo-700' },
  purple:  { bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-800',  label: 'text-purple-700' },
};

const DEFAULT_QUADRANT_COLORS: Record<string, string> = {
  topRight: 'emerald',
  topLeft: 'amber',
  bottomRight: 'blue',
  bottomLeft: 'gray',
};

function getQuadrantColorSet(
  color: string | undefined,
  position: string,
): { bg: string; border: string; text: string; label: string } {
  const key = color || DEFAULT_QUADRANT_COLORS[position] || 'gray';
  return QUADRANT_COLORS[key] || QUADRANT_COLORS.gray;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** A single quadrant cell containing a label and optional items. */
const QuadrantCell: React.FC<{
  config: QuadrantConfig;
  position: string;
}> = ({ config, position }) => {
  const colors = getQuadrantColorSet(config.color, position);

  return (
    <div className={`${colors.bg} ${colors.border} border p-4 flex flex-col min-h-[140px]`}>
      <h4 className={`text-sm font-bold ${colors.label} m-0 mb-2`}>
        {config.label}
      </h4>
      {config.items && config.items.length > 0 && (
        <ul className="list-disc list-inside m-0 p-0 space-y-1">
          {config.items.map((item, index) => (
            <li key={index} className={`text-xs ${colors.text} leading-relaxed`}>
              <span className="font-medium">{item.label}</span>
              {item.description && (
                <span className="text-gray-500 ml-1">— {item.description}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TwoByTwoMatrix: React.FC<TwoByTwoMatrixProps> = ({
  title,
  xAxisLabel,
  yAxisLabel,
  xAxisLow = 'Low',
  xAxisHigh = 'High',
  yAxisLow = 'Low',
  yAxisHigh = 'High',
  quadrants,
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* Matrix layout */}
      <div className="p-5 md:p-6">
        {/* ----------------------------------------------------------------- */}
        {/* Desktop layout: axis labels + 2x2 grid                           */}
        {/* ----------------------------------------------------------------- */}
        <div className="hidden md:block">
          <div className="flex">
            {/* Y-axis label (rotated vertically) */}
            <div className="flex items-center justify-center w-8 flex-shrink-0">
              <span
                className="text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
              >
                {yAxisLabel}
              </span>
            </div>

            {/* Y-axis high/low labels + grid */}
            <div className="flex flex-col flex-grow min-w-0">
              <div className="flex flex-grow">
                {/* Y-axis high/low labels */}
                <div className="flex flex-col justify-between w-10 flex-shrink-0 pr-2">
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right leading-none pt-2">
                    {yAxisHigh}
                  </span>
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right leading-none pb-2">
                    {yAxisLow}
                  </span>
                </div>

                {/* 2x2 grid */}
                <div className="flex-grow grid grid-cols-2 grid-rows-2 gap-0 rounded-lg overflow-hidden border border-gray-200">
                  {/* Top row */}
                  <QuadrantCell config={quadrants.topLeft} position="topLeft" />
                  <QuadrantCell config={quadrants.topRight} position="topRight" />
                  {/* Bottom row */}
                  <QuadrantCell config={quadrants.bottomLeft} position="bottomLeft" />
                  <QuadrantCell config={quadrants.bottomRight} position="bottomRight" />
                </div>
              </div>

              {/* X-axis low/high labels */}
              <div className="flex mt-2 ml-10">
                <div className="flex-grow flex justify-between px-1">
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {xAxisLow}
                  </span>
                  <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {xAxisHigh}
                  </span>
                </div>
              </div>

              {/* X-axis label (centered) */}
              <div className="flex justify-center ml-10 mt-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {xAxisLabel}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Mobile layout: stacked single column                              */}
        {/* ----------------------------------------------------------------- */}
        <div className="md:hidden space-y-3">
          {/* Axis context line */}
          <div className="text-center mb-2">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
              {yAxisLabel}: {yAxisHigh} &rarr; {yAxisLow} &nbsp;|&nbsp; {xAxisLabel}: {xAxisLow} &rarr; {xAxisHigh}
            </span>
          </div>

          {/* Quadrants stacked */}
          <div className="rounded-lg overflow-hidden border border-gray-200 divide-y divide-gray-200">
            <QuadrantCell config={quadrants.topLeft} position="topLeft" />
            <QuadrantCell config={quadrants.topRight} position="topRight" />
            <QuadrantCell config={quadrants.bottomLeft} position="bottomLeft" />
            <QuadrantCell config={quadrants.bottomRight} position="bottomRight" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoByTwoMatrix;
